import type { EditorHintRegistry, FieldHintRecord } from "./types";

/**
 * Registr nápověd editoru destinace a lokality (řez 10, typy v `./types`). Bez runtime
 * importů: měřidlo `scripts/kontrola/admin-napovedy.mjs` ho načítá přes `nactiTs`.
 *
 * Varianty: `country` = zemská úroveň (`isCountryLevel`, tedy i místo prezentované jako země,
 * Bali), `place` = lokalita pod zemí. Omezené je jen `parentInfoText` (infobox o zemi ukazuje
 * jen stránka lokality). Kotvy cílů záložek jsou literály hodnot `WEB_SECTIONS.destination`.
 *
 * Druh: hodnota, kterou web nevykresluje a jen podle ní řadí nebo přiděluje (`sortOrder`,
 * `guideId`), je `backoffice`; příznak, který prvek webu zapíná nebo skrývá (`isPublished`,
 * `showInNav`, `isFeatured`), je `web`.
 */
const DD = "apps/web/src/components/pages/DestinationDetail.astro";
const PUBLIC_DESTINATIONS = "apps/api/src/routes/public/destinations.ts";
const ACADEMY_VIDEOS = "apps/web/src/lib/academyVideos.ts";
const DESTINATION_PICKER = "apps/web/src/lib/destinationPicker.ts";
const NAVIGATION_MENU = "apps/api/src/lib/navigationMenu.ts";
const SEARCH_SUGGEST = "apps/api/src/lib/searchSuggest.ts";

/** Záznam druhu `web` s klíčem i18n `webHints.destination.<name>`. */
function web(
  name: string,
  readers: string[],
  extra: Partial<FieldHintRecord> = {},
): FieldHintRecord {
  return { kind: "web", hintKey: `webHints.destination.${name}`, readers, ...extra };
}

/** Záznam druhu `backoffice` s klíčem i18n `webHints.destination.<name>`. */
function backoffice(name: string, readers: string[]): FieldHintRecord {
  return { kind: "backoffice", hintKey: `webHints.destination.${name}`, readers };
}

const LEGACY_IMAGE =
  "starý sloupec adresy obrázku z doby před knihovnou médií; web ho čte jako zálohu, nový obsah jde přes *Id";
const NO_SK = "slovenská mutace webu zatím není";

export const destinationHintRegistry: EditorHintRegistry = {
  editor: "destination",
  table: "destinations",
  variants: ["country", "place"],
  pages: { country: "destination", place: "destination" },
  fields: {
    name: web("name", [DD]),
    slug: web("slug", [DD]),
    continent: web("continent", [
      DD,
      "apps/web/src/lib/visibleContinents.ts",
      "apps/web/src/lib/menuModel.ts",
    ]),
    parentId: web(
      "parentId",
      ["apps/web/src/lib/destinationPlaces.ts", "apps/api/src/lib/destinationPath.ts"],
      {
        variantHintKeys: { country: "webHints.destination.parentIdCountry" },
      },
    ),
    parentInfoText: web(
      "parentInfoText",
      ["apps/web/src/components/destination/CountryInfobox.astro"],
      {
        showIn: ["place"],
      },
    ),
    latitude: web("latitude", [
      "apps/web/src/lib/tripsMapPoints.ts",
      "apps/api/src/routes/public/trips.ts",
    ]),
    longitude: web("longitude", [
      "apps/web/src/lib/tripsMapPoints.ts",
      "apps/api/src/routes/public/trips.ts",
    ]),
    heroTitle: web("heroTitle", [DD]),
    heading: web("heading", [DD]),
    shortDescription: web("shortDescription", [
      "apps/web/src/lib/seoMeta.ts",
      DD,
      "apps/web/src/components/pages/MonthPage.astro",
      "apps/web/src/components/pages/InspirationDetail.astro",
      ACADEMY_VIDEOS,
    ]),
    climateInfo: web("climateInfo", [DD]),
    bestTimeToVisit: web("bestTimeToVisit", [DD]),
    description: web("description", [DD]),
    metaTitle: web("metaTitle", [DD]),
    metaDescription: web("metaDescription", [DD]),
    featuredImageId: web("featuredImageId", [DD], { readAs: ["featuredImageMedia"] }),
    guideId: backoffice("guideId", [PUBLIC_DESTINATIONS, "apps/api/src/routes/public/guides.ts"]),
    // Bez variant: varianta `country` by zahrnula i povýšené místo (`isCountryLevel`),
    // a nápověda pole proto říká obě pravidla (země bez rodiče × místo).
    sortOrder: backoffice("sortOrder", [DESTINATION_PICKER, PUBLIC_DESTINATIONS, NAVIGATION_MENU]),
    defaultMinPax: backoffice("defaultMinPax", ["apps/api/src/routes/admin/calculations.ts"]),
    autoRouteLeads: backoffice("autoRouteLeads", ["apps/api/src/lib/leadRouting.ts"]),
    isFeatured: web("isFeatured", [
      "apps/web/src/components/destinations/heroPopular.ts",
      SEARCH_SUGGEST,
      NAVIGATION_MENU,
    ]),
    // Brána publikace: veřejné API vrací jen publikované destinace, web příznak sám nečte
    // (výjimka `ctenar` v `scripts/kontrola/admin-napovedy-vyjimky/destination.json`).
    isPublished: web("isPublished", [PUBLIC_DESTINATIONS]),
    showInNav: web("showInNav", [DESTINATION_PICKER, SEARCH_SUGGEST]),
    showAsCountry: web(
      "showAsCountry",
      // `/destinace` zužuje na zemskou úroveň v `countriesInDisplayOrder` (visibleContinents.ts)
      [DESTINATION_PICKER, "apps/web/src/lib/visibleContinents.ts"],
      {
        readAs: ["isCountryLevel"],
      },
    ),
    highlights: web("highlights", [DD]),
    regions: web("regions", [
      "apps/web/src/lib/destinationPlaces.ts",
      "apps/api/src/lib/agent/context.ts",
    ]),
    testimonial: web("testimonial", [DD, "apps/web/src/lib/testimonialSignature.ts"]),
    additionalContent: web("additionalContent", [DD]),
    additionalImageId: web("additionalImageId", [DD], { readAs: ["additionalImageMedia"] }),
    "practicalInfo.visa": web("practicalInfoVisa", [DD]),
    "practicalInfo.currency": web("practicalInfoCurrency", [DD]),
    "practicalInfo.language": web("practicalInfoLanguage", [DD]),
    "practicalInfo.timezone": web("practicalInfoTimezone", [DD]),
    "practicalInfo.electricity": web("practicalInfoElectricity", [DD]),
    "practicalInfo.vaccination": web("practicalInfoVaccination", [DD]),
    weatherData: web("weatherData", [DD, "apps/api/src/routes/public/months.ts"]),
    cuisine: web("cuisine", [DD]),
    activities: web("activities", [DD]),
    whyWithUs: web("whyWithUs", [DD]),
    heroVideoUrl: web("heroVideoUrl", [DD, ACADEMY_VIDEOS]),
    videoPromo: web("videoPromo", [DD, ACADEMY_VIDEOS]),
    moreVideosUrl: web("moreVideosUrl", [DD]),
    galleryImageIds: web("galleryImageIds", [DD], { readAs: ["galleryMedia"] }),
    socialReels: web("socialReels", [DD]),
    faqs: web("faqs", [DD]),
    "offerContent.*.intro": web("offerIntro", [
      "apps/web/src/lib/tripsListing.ts",
      "apps/api/src/lib/offerPages.ts",
    ]),
    "offerContent.*.faqs": web("offerFaqs", ["apps/web/src/lib/tripsListing.ts"]),
    planningBlockVariant: web("planningBlockVariant", [DD], {
      readAs: ["resolvePlanningBlockText"],
    }),
    planningBlockText: web("planningBlockText", [DD], { readAs: ["resolvePlanningBlockText"] }),
    inspirationIds: web("inspirationIds", [DD], { readAs: ["inspirations"] }),
  },
  columnsOutsideEditor: {
    id: { status: "system", reason: "technický sloupec" },
    createdAt: { status: "system", reason: "technický sloupec" },
    updatedAt: { status: "system", reason: "technický sloupec" },
    featuredImage: { status: "system", reason: LEGACY_IMAGE },
    galleryImages: { status: "system", reason: LEGACY_IMAGE },
    additionalImage: { status: "system", reason: LEGACY_IMAGE },
    nameSk: { status: "necte", reason: NO_SK },
    shortDescriptionSk: { status: "necte", reason: NO_SK },
    descriptionSk: { status: "necte", reason: NO_SK },
    cuisineSk: { status: "necte", reason: NO_SK },
    relatedInspirations: {
      status: "necte",
      reason: "mrtvé pole, vazbu na inspirace nese activities.destinationId",
    },
    relatedTrips: {
      status: "necte",
      reason: "mrtvé pole, zájezdy destinace se počítají z trips",
    },
    garantId: {
      status: "bezPolicka",
      reason:
        "garant destinace pro směrování poptávek a frontu služeb (serviceBookings.ts); políčko chybí, web ho nečte",
    },
    region: {
      status: "bezPolicka",
      reason: "filtr API ?region=, web ho neposílá; políčko chybí",
    },
  },
  tabs: {
    basic: { country: { anchor: "popis" }, place: { anchor: "popis" } },
    content: { country: { anchor: "kam-vyrazit" }, place: { anchor: "kam-vyrazit" } },
    practical: { country: { anchor: "tipy" }, place: { anchor: "tipy" } },
    cuisine: { country: { anchor: "tipy" }, place: { anchor: "tipy" } },
    media: { country: { anchor: "video" }, place: { anchor: "video" } },
    faq: { country: { anchor: "tipy" }, place: { anchor: "tipy" } },
    offer: { country: { page: "offer" }, place: { page: "offer" } },
    related: { country: { anchor: "aktivity" }, place: { anchor: "aktivity" } },
  },
};
